import { useEffect, useState } from 'react';

/**
 * useInstallPrompt — shared state for the PWA install flow.
 *
 * Two consumers (the InstallHint banner and the Install row inside
 * Settings) both need to know:
 *   - whether the browser has fired `beforeinstallprompt` (the deferred
 *     event we can trigger later)
 *   - whether we're on iOS (no install API; user must Add-to-Home-Screen)
 *   - whether the app is already running standalone
 *   - whether the user has dismissed the banner (Settings row stays
 *     visible regardless)
 *
 * `beforeinstallprompt` only fires once per page load and only one
 * caller can consume it. So we capture it at module scope as a tiny
 * pub/sub singleton — multiple components can subscribe via this hook
 * and all see the same state. Walter 2026-05-15: built so the user
 * can dismiss the auto-banner without losing access; the Settings
 * row reads from the same singleton.
 */

// Module-level singleton state. Lives for the lifetime of the page.
const state = {
  deferredPrompt: null,   // BeforeInstallPromptEvent | null
  installed: false,       // appinstalled fired during this session
};

const listeners = new Set();
const notify = () => { for (const fn of listeners) fn(); };

// One-shot init: attach the window listeners exactly once when this
// module is first imported. Guarded so HMR re-imports don't double-bind.
let initialized = false;
function initOnce() {
  if (initialized) return;
  if (typeof window === 'undefined') return;
  initialized = true;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    state.deferredPrompt = e;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    state.installed = true;
    state.deferredPrompt = null;
    notify();
  });
}
initOnce();

// ─── platform detection helpers ─────────────────────────────────────

export function isIOSDevice() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // iPad on iOS 13+ reports as Mac, so detect via touch points too.
  return /iPhone|iPad|iPod/.test(ua)
    || (ua.includes('Mac') && navigator.maxTouchPoints > 1);
}

export function isStandaloneNow() {
  if (typeof window === 'undefined') return false;
  return window.navigator.standalone === true
    || (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);
}

// ─── the hook ───────────────────────────────────────────────────────

/**
 * Returns an object with:
 *   - canInstall: true if browser has offered a deferred prompt
 *   - isIOS: true on iOS devices (no install API; manual Add-to-Home-Screen)
 *   - isStandalone: true if the app is currently in standalone mode
 *   - installed: true if appinstalled fired during this session
 *   - prompt(): triggers the browser install dialog (Chrome/Edge/Android).
 *               Returns { outcome: 'accepted' | 'dismissed' }. No-op on iOS.
 */
export function useInstallPrompt() {
  const [, force] = useState(0);

  useEffect(() => {
    const cb = () => force((n) => n + 1);
    listeners.add(cb);
    return () => { listeners.delete(cb); };
  }, []);

  const prompt = async () => {
    const e = state.deferredPrompt;
    if (!e) return { outcome: 'unavailable' };
    try {
      await e.prompt();
      const choice = await e.userChoice;
      // The event is single-use — once prompted, the browser won't
      // re-fire it. Clear it so consumers re-render to the
      // "no longer installable" state.
      state.deferredPrompt = null;
      notify();
      return choice || { outcome: 'unavailable' };
    } catch (err) {
      state.deferredPrompt = null;
      notify();
      return { outcome: 'unavailable' };
    }
  };

  return {
    canInstall: !!state.deferredPrompt,
    isIOS: isIOSDevice(),
    isStandalone: isStandaloneNow(),
    installed: state.installed,
    prompt,
  };
}
